import { useCallback, useEffect, useRef, useState } from "react";
import {
  type Access,
  type CurrentUserResult,
  fetchCurrentUser,
  type PlanOffer,
} from "@/lib/auth-api";

export type SessionState =
  | { status: "checking" }
  | { status: "anonymous" }
  | {
      status: "authenticated";
      login: string;
      canInvite: boolean;
      isOwner: boolean | null;
      access: Access | null;
      /** Состав тарифов для текста «Доступно на тарифе …». */
      plans: PlanOffer[];
    }
  | { status: "unavailable" };

/**
 * Сессия сразу после входа: право приглашать — «нет», владелец — `null`
 * (ещё не известно, §130 п. 5): пока демон не ответил на перечтение `me`,
 * раздел «Аккаунт» не предлагает удаление. Доступ по тарифу — тоже `null`:
 * платная стена не решает, пока `me` не перечитан.
 */
export function freshSession(login: string): SessionState {
  return {
    status: "authenticated",
    login,
    canInvite: false,
    isOwner: null,
    access: null,
    plans: [],
  };
}

/**
 * Состояние после перечтения `me` после входа. Недоступность и чужой логин
 * состояние не трогают: право остаётся «нет», владелец — `null`.
 */
export function sessionAfterReread(
  current: SessionState,
  login: string,
  result: CurrentUserResult,
): SessionState {
  if (result.status !== "authenticated" || result.user.login !== login) {
    return current;
  }
  if (current.status !== "authenticated" || current.login !== login) {
    return current;
  }
  return {
    status: "authenticated",
    login,
    canInvite: result.user.can_invite,
    isOwner: result.user.is_owner,
    access: result.user.access,
    plans: result.user.plans,
  };
}

/** Паузы перед первыми повторами перечтения `me` после сбоя: 1 с, 3 с, 10 с. */
export const REREAD_RETRY_DELAYS_MS: readonly number[] = [1000, 3000, 10_000];
/** Пауза между повторами после первых трёх: дальше каждые 30 с, пока не ответит. */
export const REREAD_STEADY_DELAY_MS = 30_000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export interface RereadControl {
  /** Цепочка больше не нужна (устарела, сессия ушла, хук размонтирован): выйти без ответа. */
  shouldStop?: () => boolean;
  /** Вызывается один раз, на первой неудаче серии. */
  onFirstFailure?: () => void;
  delays?: readonly number[];
  steadyDelayMs?: number;
}

/**
 * Перечитывает `me`, пока демон не ответит (в том числе «анонимен»): паузы
 * 1 с, 3 с, 10 с, затем каждые 30 с. Возвращает `null`, только если
 * `shouldStop` велел выйти: иначе замки показывали бы старый тариф, пока
 * поток уже идёт по новому.
 */
export async function rereadWithRetry(
  fetchMe: () => Promise<CurrentUserResult>,
  wait: (ms: number) => Promise<void>,
  control: RereadControl = {},
): Promise<CurrentUserResult | null> {
  const {
    shouldStop = () => false,
    onFirstFailure,
    delays = REREAD_RETRY_DELAYS_MS,
    steadyDelayMs = REREAD_STEADY_DELAY_MS,
  } = control;
  let failures = 0;
  while (!shouldStop()) {
    const result = await fetchMe();
    if (shouldStop()) {
      return null;
    }
    if (result.status !== "unavailable") {
      return result;
    }
    failures += 1;
    if (failures === 1) {
      onFirstFailure?.();
    }
    await wait(delays[failures - 1] ?? steadyDelayMs);
  }
  return null;
}

/**
 * Проверяет сессию при запуске через `GET /api/auth/me` и держит текущее
 * состояние входа. До ответа состояние — `checking`: экран не решает,
 * форму показывать или данные, пока ответа нет. Недоступность демона —
 * отдельное состояние `unavailable`, а не то же самое, что отсутствие
 * сессии: смешение выбрасывало бы вошедшего человека на форму, где вход
 * тоже не работает.
 */
export function useSession() {
  const [session, setSession] = useState<SessionState>({ status: "checking" });
  /** Поколение перечтения: новый `rereadMe` и размонтирование гасят старую цепочку. */
  const rereadGeneration = useRef(0);
  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);
  useEffect(
    () => () => {
      rereadGeneration.current += 1;
    },
    [],
  );

  const applyResult = useCallback((result: CurrentUserResult) => {
    switch (result.status) {
      case "authenticated":
        setSession({
          status: "authenticated",
          login: result.user.login,
          canInvite: result.user.can_invite,
          isOwner: result.user.is_owner,
          access: result.user.access,
          plans: result.user.plans,
        });
        return;
      case "anonymous":
        setSession({ status: "anonymous" });
        return;
      case "unavailable":
        setSession({ status: "unavailable" });
        return;
      default:
        return result satisfies never;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchCurrentUser().then((result) => {
      if (cancelled) {
        return;
      }
      applyResult(result);
    });
    return () => {
      cancelled = true;
    };
  }, [applyResult]);

  /**
   * Вход или регистрация удались. Право приглашать после них неизвестно —
   * входим без него и сразу же дочитываем у демона `GET /api/auth/me`: так
   * человек с правом видит «Пригласить» без перезагрузки. Недоступность
   * дочитывания вошедшего не выбрасывает: право остаётся «нет».
   */
  const markAuthenticated = useCallback((login: string) => {
    setSession(freshSession(login));
    fetchCurrentUser().then((result) => {
      setSession((current) => sessionAfterReread(current, login, result));
    });
  }, []);

  /**
   * Перечитать `me` без смены статуса: доступ изменился при открытом потоке
   * (код закрытия «доступ изменился»). Повторяет до ответа демона; выходит,
   * если начался более новый `rereadMe`, сессия ушла из `authenticated` или
   * хук размонтирован.
   */
  const rereadMe = useCallback(() => {
    rereadGeneration.current += 1;
    const generation = rereadGeneration.current;
    const shouldStop = () =>
      rereadGeneration.current !== generation || sessionRef.current.status !== "authenticated";
    rereadWithRetry(fetchCurrentUser, sleep, {
      shouldStop,
      onFirstFailure: () =>
        console.error(
          "не удалось перечитать me после смены доступа: повторяю, пока демон не ответит",
        ),
    }).then((result) => {
      if (result === null || shouldStop()) {
        return;
      }
      setSession((current) =>
        current.status === "authenticated"
          ? sessionAfterReread(current, current.login, result)
          : current,
      );
    });
  }, []);

  const markAnonymous = useCallback(() => {
    setSession({ status: "anonymous" });
  }, []);

  /** Повторная проверка сессии — кнопка «Повторить» на экране недоступности демона. */
  const retryCheck = useCallback(() => {
    setSession({ status: "checking" });
    fetchCurrentUser().then(applyResult);
  }, [applyResult]);

  return { session, markAuthenticated, markAnonymous, retryCheck, rereadMe };
}
